import {createAsyncThunk, createSlice, current} from "@reduxjs/toolkit";
import type {PayloadAction} from "@reduxjs/toolkit";
import {FormStep, FormData, FormState, InitialData} from "@/lib/types/upgrad/upgradForm"
import {Transform} from "@/lib/helper";
import {
    DEFAULT_INTEREST_RATE, DEFAULT_SERVICE_RATE,
    InterestRate, Rate,
    UPGRAD_LOAN_OVERVIEW,
    UPGRAD_LOAN_SCHEDULE, UpgradLoanOverview, UpgradLoanSchedule
} from "@/lib/types/calulator/upgrad";
import {DEFAULT_UPGRAD_COURSE, UpGradCourse} from "@/lib/types/upgrad/course";
import {CalculatorService, CourseService, UpgradService} from "@/services";
import {DEFAULT_EXCHANGE_CURRENCY, ExchangeCurrency} from "@/lib/types/config";

const formStep: FormStep = {
    currentStep: 0,
    steps: [
        {
            title: 'Thông tin học phí',
            time: '3 phút',
            fields: [ "email","courseType","university","delayDuration","duration","prepayAdditionalAmount" ]
        },
        {
            title: 'Xác thực định danh',
            time: '5 phút',
            fields: []
        },
        {
            title: 'Thông tin người đồng kí',
            time: '1 phút',
            fields: ['cosignerName', 'cosignerRelationship', 'cosignerEmail', 'cosignerPhoneNumber']
        },
        {
            title: 'Thông tin hồ sơ',
            time: '3 phút',
            fields: ['bankAccount', 'houseBook', 'laborContract', 'marriageRegistration', 'vssid']
        },
        {
            title: 'Xác nhận và thanh toán',
            time: '3 phút',
            fields: []
        },
    ]
}

const formData: FormData = {
    step1: {
        email: '',
        courseType: '',
        university: '',
        delayDuration: 0,
        duration: 6,
        prepayAdditionalAmount: 0
    },
    step2: {
        face: {
            image: "",
            status: "idle",
            message: "Chụp ảnh chân dung của bạn",
        },
        idFront: {
            image: "",
            status: "disabled",
            message: "Chụp ảnh mặt trước của CCCD",
        },
        idBack: {
            image: "",
            status: "disabled",
            message: "Chụp ảnh mặt sau của CCCD",
        },
        faceMatching: {
            status: 'disabled',
            message: '',
            idData: {}
        }
    },
    step3: {
        cosignerName: '',
        cosignerEmail: '',
        cosignerPhoneNumber: '',
        cosignerRelationship: ''
    },
    step4: {
        houseBook: "",
        vssid: "",
        bankAccount: "",
        laborContract: "",
        marriageRegistration: ""
    }
};

const initialState: FormState = {
    formStep: formStep,
    formData: formData,
    loanData: {
        loanOverview: UPGRAD_LOAN_OVERVIEW,
        loanSchedule: UPGRAD_LOAN_SCHEDULE,
        course: DEFAULT_UPGRAD_COURSE,
        exchangeCurrency: DEFAULT_EXCHANGE_CURRENCY
    },
    initialData: {
        rates: {interest_rate:[DEFAULT_INTEREST_RATE],service_rate: DEFAULT_SERVICE_RATE},
        exchangeCurrencies: [DEFAULT_EXCHANGE_CURRENCY],
        serviceRate: 0,
        courses: [DEFAULT_UPGRAD_COURSE],
        courseTypes: [{label: "", value: ""}],
        universities: [{label: "", value: ""}],
        months: [6, 9, 12, 15, 18, 21, 24, 27, 30, 33, 36],
        delayMonths: [0, 1, 2, 3]
    }
};

export const upgradFormSlice = createSlice({
    name: "upgradForm",
    initialState,
    reducers: {
        calculateLoanData: (state, actions: PayloadAction<{courseType:string,university:string,delayDuration:number,duration:number,prepayAdditionalAmount:number}>) => {
            const data = actions.payload;
            const upgradService = new UpgradService(current(state.initialData.rates));
            const calculateService = new CalculatorService()
            const courseService = new CourseService()
            const course = courseService.getCourse(
                current(state.initialData.courses),
                data.courseType,
                data.university
            )
            const amount = calculateService.convertAmountCurrency(
                course.on_campus_fee,
                course.on_campus_currency,
                current(state.initialData.exchangeCurrencies)
            )
            const exchangeCurrency = calculateService.getExchangeCurrency(course.on_campus_currency, current(state.initialData.exchangeCurrencies));
            const loanOverview = upgradService.calculateLoanOverview(amount,data.duration,data.prepayAdditionalAmount,data.delayDuration)
            const loanSchedule = upgradService.calculateLoanSchedule(amount,data.duration,data.prepayAdditionalAmount,data.delayDuration)
            state.loanData.loanOverview = loanOverview
            state.loanData.loanSchedule = loanSchedule
            state.loanData.course = course
            state.loanData.exchangeCurrency = exchangeCurrency
        },
        updateDelayMonths: (state, actions: PayloadAction<number>) => {
            let delayMonths:number[] = []
            for(let month = 0; month <= actions.payload; month++){
                delayMonths = [...delayMonths, month];
            }
            state.initialData.delayMonths = delayMonths;
        },
        setInitialData: (state, actions: PayloadAction<{
            rates: Rate,
            serviceRate: number,
            courses: UpGradCourse[],
            exchangeCurrencies: ExchangeCurrency[]
        }>) => {
            const courseService = new CourseService();
            state.initialData.rates = actions.payload.rates;
            state.initialData.serviceRate = actions.payload.serviceRate;
            state.initialData.courses = actions.payload.courses;
            state.initialData.exchangeCurrencies = actions.payload.exchangeCurrencies;
            state.initialData.courseTypes = courseService.getCourseTypeOptions(actions.payload.courses)
            state.initialData.universities = courseService.getUniversityOptions(actions.payload.courses)
        },
        updateFormData: (state, actions: PayloadAction<{ step: number, data: any }>) => {
            let stepName = 'step' + actions.payload.step;
            // @ts-ignore
            state.formData[stepName] = actions.payload.data;
        },
        changeStep: (state, actions: PayloadAction<number>) => {
            state.formStep.currentStep = actions.payload
        },
        validateImageEkyc: (
            state,
            actions: PayloadAction<{
                file: any,
                status: 'idle' | 'disabled' | 'pass' | 'fail' | 'loading',
                message: string,
                type: 'face' | 'idFront' | 'idBack'
            }>
        ) => {
            //Update current type data
            let payload = actions.payload
            let ekycData = {
                image: payload.file,
                status: payload.status,
                message: payload.message
            }
            state.formData.step2[payload.type] = ekycData

            //Enable next input
            if (payload.status == 'pass') {
                switch (payload.type) {
                    case 'face':
                        state.formData.step2.idFront.status = 'idle'
                        break;
                    case 'idFront':
                        state.formData.step2.idBack.status = 'idle'
                        break;
                    case 'idBack':
                        state.formData.step2.faceMatching.status = 'idle'
                        break;
                    default:
                        break;
                }
            }
        },
        faceMatchingEkyc: (
            state,
            actions: PayloadAction<{
                status: 'idle' | 'disabled' | 'pass' | 'fail' | 'loading',
                message: string,
                information?: any
            }>
        ) => {
            let payload = actions.payload
            state.formData.step2.faceMatching = {
                status: payload.status,
                message: payload.message,
                idData: payload.information ?? {}
            }
        }
    },
});

export const {calculateLoanData, updateDelayMonths, setInitialData, validateImageEkyc, faceMatchingEkyc, changeStep, updateFormData} =
    upgradFormSlice.actions;
export default upgradFormSlice;
